import styled from '@emotion/styled';
import { Button, Tooltip, TooltipBody, useTooltip } from '@smartsheet/lodestar-core';
import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import exportContent from '../../../assets/images/download.svg';
import { AsyncStatus } from '../../../common/enums';
import { UserActions } from '../../../common/enums/UserActions.enum';
import { Complexity } from '../../../common/interfaces/report';
import { UserAnalyticsAction } from '../../../common/metrics/UserAnalyticsAction';
import { getAllReportRows } from '../../../http-clients/ReportClient';
import { useLanguageElements } from '../../../language-elements/withLanguageElementsHOC';
import { useToastManager } from '../../App/ToastProvider';
import { userLocaleSelector } from '../../Auth/Selectors';
import { Actions } from '../Report/Actions';
import { PAGINATION_LIMIT } from '../Report/constants';
import { isReportDataStaleSelector, reportColumnsSelector, reportJobSelector } from '../Report/Selectors';
import { downloadCsvContent, generateCsvContent } from './DownloadUtils';

export interface DownloadProps {
    viewId: string;
    viewSourceId: number;
    filename?: string;
}

enum DownloadStatus {
    NOT_READY = 'NOT_READY',
    READY = 'READY',
    LOADING_REPORT_JOB = 'LOADING_REPORT_JOB',
    FETCHING_DATA = 'FETCHING_DATA',
    ERROR = 'ERROR',
}

export const Download: React.FC<DownloadProps> = ({ viewId, viewSourceId, filename }) => {
    const dispatch = useDispatch();
    const languageElements = useLanguageElements();
    const { toastManager } = useToastManager();
    const { tooltipProps, targetProps } = useTooltip({
        placement: 'auto',
    });
    const reportJob = useSelector(reportJobSelector);
    const isReportDataStale = useSelector(isReportDataStaleSelector);
    const reportColumnsResult = useSelector(reportColumnsSelector);
    const userLocale = useSelector(userLocaleSelector);

    const [status, setStatus] = useState<DownloadStatus>(DownloadStatus.NOT_READY);

    const fetchAndDownloadCsv = useCallback(async () => {
        if (reportJob.status !== AsyncStatus.DONE || reportColumnsResult.status !== AsyncStatus.DONE) {
            setStatus(DownloadStatus.NOT_READY);
            return;
        }

        setStatus(DownloadStatus.FETCHING_DATA);

        // Fetch all report rows
        const allRows = await getAllReportRows(viewId, reportJob.data.jobId, PAGINATION_LIMIT);

        const csvContent = generateCsvContent(reportColumnsResult.data, allRows, userLocale);

        // Download the CSV content
        downloadCsvContent(filename || languageElements.VIEW_DOWNLOAD_DEFAULT_FILENAME, csvContent);

        setStatus(DownloadStatus.READY);
    }, [filename, languageElements.VIEW_DOWNLOAD_DEFAULT_FILENAME, reportColumnsResult, reportJob, userLocale, viewId]);

    useEffect(() => {
        if (reportJob.status !== AsyncStatus.DONE || reportColumnsResult.status !== AsyncStatus.DONE) {
            setStatus(DownloadStatus.NOT_READY);
            return;
        }

        switch (status) {
            case DownloadStatus.LOADING_REPORT_JOB:
                fetchAndDownloadCsv();
                break;
            case DownloadStatus.NOT_READY:
                setStatus(DownloadStatus.READY);
                break;
        }
    }, [reportColumnsResult.status, reportJob.status, status, fetchAndDownloadCsv]);

    const handleClick = (event: React.SyntheticEvent): void => {
        // Submit user analytics event
        UserAnalyticsAction.addFromEvent(
            event,
            {
                viewId,
                viewSourceId,
            },
            UserActions.CLICK_EXPORT_VIEW
        );

        // Show toast if the report complexity is not small (i.e., it may take time to generate the report)
        if (reportJob.status === AsyncStatus.DONE && reportJob.data.complexity !== Complexity.SMALL) {
            toastManager.createSimpleToast({
                message: languageElements.DOWNLOAD_PROGRESS_MESSAGE,
                type: 'info',
            });
        }

        if (isReportDataStale) {
            dispatch(
                Actions.loadReportJob({
                    viewId,
                    page: 0,
                })
            );

            setStatus(DownloadStatus.LOADING_REPORT_JOB);
            return;
        }

        fetchAndDownloadCsv();
    };

    return (
        <>
            <StyledButton
                onClick={handleClick}
                appearance={'secondary-empty'}
                size="xsmall"
                iconBefore={<ExportImage src={exportContent} alt={languageElements.DOWNLOAD_ALT_TEXT} />}
                {...targetProps}
                isLoading={status === DownloadStatus.LOADING_REPORT_JOB || status === DownloadStatus.FETCHING_DATA}
                isDisabled={status === DownloadStatus.NOT_READY}
                hideEmptyContent={true}
                aria-label={languageElements.DOWNLOAD_ALT_TEXT}
            />
            <Tooltip {...tooltipProps}>
                <TooltipBody>{languageElements.VIEW_DOWNLOAD_VIEW_DATA}</TooltipBody>
            </Tooltip>
        </>
    );
};

const StyledButton = styled(Button)`
    height: 26px;
`;

const ExportImage = styled.img`
    height: 16px;
`;
